import React, { useState, useEffect, useRef } from "react";
import { useSetRecoilState } from "recoil";
import PropTypes from "prop-types";
import classNames from "classnames";
import tableState from "../../Atoms/Table/state";

import { DateUtils } from "react-day-picker";
import DayPickerInput from "react-day-picker/DayPickerInput";
import "react-day-picker/lib/style.css";
import { IoIosCalendar, IoIosClose } from "react-icons/io";
import { isAfter } from "date-fns";
import dateFnsFormat from "date-fns/format";

import "react-datepicker/dist/react-datepicker.css";
import "./DateInput.scss";

const DateInput = ({
  schemaProps: {
    machine_name,
    hideIcons,
    noFixedWidth,
    minDate,
    maxDate,
    clearSchema,
    addTableError,
    parent_machine_name,
    dynamicUpdates,
    updateOnChange,
  },
  width,
  input,
  formApi,
  formValues,
  disabled,
  disabledViaReuse,
}) => {
  const dateRef = useRef();

  useEffect(() => {
    formApi.mutators.setFieldTouched(input.name, true);
  }, []);

  /**
   * Conditionally sets another field based on date selection.
   *
   * This was used in 790A on Job Offer Information to dynamically set form version baesd on date selected and,
   * it is no longer being used.
   */
  const dynamicallyUpdate = (value) => {
    let formVersionId = formApi?.getState()?.values?.formVersionId;
    let newId;

    let isDateValid = false;
    if (
      (typeof value === "string" && value?.length === 10) ||
      typeof value === "object"
    ) {
      isDateValid = DateUtils.isDate(new Date(value));
    }
    //standardize dates
    //ensure same timezones EST for comparison
    if (Array.isArray(dynamicUpdates) && value) {
      for (let update of dynamicUpdates) {
        // setting something to based on value

        if (
          update.hasOwnProperty("to") &&
          update.hasOwnProperty("set") &&
          isDateValid
        ) {
          switch (update.operator) {
            case ">=":
              let selectedDate = new Date(value);
              let compareDate = new Date(update.if);

              if (isAfter(selectedDate, compareDate)) {
                formApi.change(update.set, update.to);
                newId = update.to;
              } else if (update.hasOwnProperty("elseTo")) {
                formApi.change(update.set, update.elseTo);
                newId = update.elseTo;
              }
              break;
            default:
              if (value === update.if) {
                formApi.change(update.set, update.to);
              }
          }
        }

        // clearing schema based on value
        if (
          update.hasOwnProperty("if") &&
          update.hasOwnProperty("clearSchema")
        ) {
          switch (update.operator) {
            case "!=":
              if (value !== update.if) {
                update.clearSchema.forEach((c) => {
                  formApi.change(c, null);
                });
              }
              break;
            default:
              if (value === update.if) {
                update.clearSchema.forEach((c) => {
                  formApi.change(c, null);
                });
              }
          }
        }

        // clearing files based on value
        if (
          update.hasOwnProperty("if") &&
          update.hasOwnProperty("clearFiles")
        ) {
          const currentFiles =
            formApi.getState().values && formApi.getState().values["files"];
          const hasFiles =
            currentFiles &&
            Array.isArray(currentFiles) &&
            currentFiles.length > 0;
          let shouldClear = false;
          switch (update.operator) {
            case "!=":
              shouldClear = value != update.if;
              break;
            default:
              shouldClear = value == update.if;
          }

          if (hasFiles && shouldClear) {
            const updatedFiles = currentFiles.filter(
              (file) => !update.clearFiles.includes(file.machine_name)
            );
            currentFiles.filter((file) => {
              return update.clearFiles.includes(file.machine_name);
            });
            if (
              currentFiles.filter((file) =>
                update.clearFiles.includes(file.machine_name)
              ).length > 0
            ) {
              formApi.change("files", updatedFiles);
            }
          }
        }
      }
    }
    // ResetFields property to clear based on FormVersionId
    if (
      isDateValid &&
      formVersionId !== newId &&
      dynamicUpdates[0]?.resetFields
    ) {
      let arr = dynamicUpdates[0]?.resetFields;
      for (let i = 0; i < arr.length; i++) {
        if (typeof arr[i] === "object") {
          for (let prop in arr[i]) {
            formApi.change(prop, []);
          }
        } else {
          formApi.change(arr[i], null);
        }
      }
    }
  };

  const setErrors = useSetRecoilState(tableState);
  const setTableErrors = (val, error, machineName, rowIndex, columnId) => {
    let newTableErrors;
    if (error) {
      setErrors((errors) => {
        newTableErrors = JSON.parse(JSON.stringify(errors));
        !(machineName in newTableErrors) &&
          (newTableErrors[`${machineName}`] = {});
        // rowKey exists? if not create it
        !(rowIndex in newTableErrors[`${machineName}`]) &&
          (newTableErrors[`${machineName}`][`${rowIndex}`] = []);
        // item in array exists? if not push it
        !newTableErrors[`${machineName}`][`${rowIndex}`].includes(columnId) &&
          newTableErrors[`${machineName}`][`${rowIndex}`].push(columnId);
        return newTableErrors;
      });
    }
  };
  const clearFields = () => {
    clearSchema.forEach((c) => {
      // clearing field on fieldArray
      if (c.includes(".")) {
        const [fieldArray, fieldArrayField] = c.split(".");
        const formValues = formApi.getState().values;
        if (
          formValues &&
          fieldArray &&
          Array.isArray(formValues[fieldArray]) &&
          formValues[fieldArray].length
        ) {
          const fieldArrayRows = formValues[fieldArray];
          fieldArrayRows.forEach((row, index) => {
            const fieldToClear = `${fieldArray}[${index}].${fieldArrayField}`;
            formApi.change(fieldToClear, null);
          });
        }
      }
      // clearing simple field
      else {
        let fieldToClear = parent_machine_name
          ? `${parent_machine_name}[0]${c}`
          : c;
        formApi.change(fieldToClear, null);
      }
    });
  };

  const addTableFieldError = () => {
    addTableError.forEach((c) => {
      const [fieldArray, fieldArrayField] = c.split(".");
      const formValues = formApi.getState().values;
      const fieldArrayRows = formValues[fieldArray];
      fieldArrayRows &&
        fieldArrayRows.forEach((_field, index) => {
          setTableErrors(
            null,
            "This field is required",
            fieldArray,
            index,
            fieldArrayField
          );
        });
    });
  };

  // date stuff
  const formatDate = (day, format, locale) => {
    return dateFnsFormat(day, format, { locale });
  };

  const [day, setDay] = useState(
    input.value ? dateFnsFormat(new Date(input.value), "MM/dd/yyyy") : null
  );
  const [key, setKey] = useState(machine_name);

  // Allows datepicker's value to update on form value changes, ex: prefill
  useEffect(() => {
    if (!updateOnChange || !input?.value) return;
    setDay(dateFnsFormat(new Date(input.value), "MM/dd/yyyy"));
  }, [input?.value]);

  const handleChange = (day, _, dayPickerInput) => {
    dynamicUpdates && dynamicallyUpdate(day);
    clearSchema && clearFields();
    addTableError && addTableFieldError();
    formApi.mutators.setFieldTouched(input.name, true);
    const dayInput = dayPickerInput.getInput();
    if (!day && dayInput?.value) {
      dynamicUpdates && dynamicallyUpdate(dayInput.value);
      let inputDate = new Date(dayInput.value + " 12:00:00");
      if (DateUtils.isDate(inputDate) && dayInput.value.length === 10) {
        setDay(inputDate);
        input.onChange(inputDate);
      } else {
        input.onChange(null);
      }
    }

    if (day) {
      setDay(day);
      input.onChange(day);
    }
  };

  const handleClear = (e) => {
    setDay(null);
    input.onChange(null);
    dateRef.current.input.value = "";
    setKey(`${machine_name}_${new Date().getTime()}`);
  };

  let minDateValue;
  let maxDateValue;
  let formState = formApi.getState();

  if (minDate && formState && formState.values && formState.values[minDate]) {
    minDateValue = formState.values[minDate];
  }
  if (maxDate && formState && formState.values && formState.values[maxDate]) {
    maxDateValue = formState.values[maxDate];
  }
  let wrapperClass = classNames("grid-row", "grid-gap", {
    // 'input-hide' : (shouldHide === true ),
    // 'input-conditional': isConditionalField
  });

  let formRowClasses = classNames("form-row-dimension", {
    [`mobile-lg:grid-col-${width}`]: true,
  });

  let disabledDays = [];
  if (maxDateValue) {
    disabledDays = [
      ...disabledDays,
      {
        after: new Date(maxDateValue),
      },
    ];
  }
  if (minDateValue) {
    disabledDays = [
      ...disabledDays,
      {
        before: new Date(minDateValue),
      },
    ];
  }

  let initialMonth = minDateValue || maxDateValue;
  return (
    <div className={wrapperClass}>
      <div
        className={formRowClasses}
        style={{ marginRight: hideIcons ? "0px" : "20px" }}
      >
        <label
          className="usa-label"
          htmlFor={machine_name}
          style={{ position: "relative" }}
        >
          <DayPickerInput
            ref={dateRef}
            className="root-day-picker"
            value={day}
            placeholder="mm/dd/yyyy"
            format="MM/dd/yyyy"
            formatDate={formatDate}
            onDayChange={handleChange}
            onBlur={(e) => {
              formApi.mutators.setFieldTouched(input.name, true);
            }}
            style={
              noFixedWidth
                ? { minWidth: "110px", border: "1px !important" }
                : { width: "200px", border: "1px !important" }
            }
            dayPickerProps={{
              initialMonth: initialMonth ? new Date(initialMonth) : new Date(),
              disabledDays,
            }}
            inputProps={{
              key: key,
              id: machine_name,
              name: machine_name,
              disabled: !!disabledViaReuse ? false : disabled,
            }}
          />

          {!hideIcons && (
            <IoIosCalendar
              style={{
                right: "8px",
                opacity: 0.5,
                top: "2px",
                position: "absolute",
              }}
            />
          )}
          {input.value && !hideIcons && !disabled && (
            <IoIosClose
              size={28}
              style={{
                right: "-28px",
                opacity: 0.5,
                top: "50%",
                transform: "translateY(-50%)",
                position: "absolute",
                cursor: "pointer",
                size: "20px",
              }}
              onClick={handleClear}
            />
          )}
        </label>
      </div>
    </div>
  );
};

DateInput.defaultProps = {
  id: Math.floor(Math.random() * 100000),
  tooltip:
    "There is an error displaying this tooltip. Contact the system administrator.",
};

DateInput.propTypes = {
  tooltip: PropTypes.string,
  label: PropTypes.string.isRequired,
  width: PropTypes.number,
};

export default DateInput;
